<template>
  <div class="rb-ob-form rb-match-page rb-book-page">
    <header><h1 class="rb-ob-title">{{ isCareSession ? (isFa ? 'لغو جلسه' : 'Cancel session') : (isFa ? 'لغو رزرو' : 'Cancel booking') }}</h1></header>
    <p v-if="needsLogin" class="rb-ob-status" role="status">{{ $t('intake.portal.needsLogin') }}</p>
    <p v-else-if="loading" class="rb-ob-status" role="status">{{ $t('intake.portal.loading') }}</p>

    <ul v-if="booking" class="rb-book-summary" aria-label="Booking cancellation summary">
      <li><span class="rb-book-summary-k">{{ isFa ? 'کد رزرو' : 'Booking' }}</span><span class="rb-book-summary-v"><bdi>{{ booking.booking_code || booking.booking_id }}</bdi></span></li>
      <li><span class="rb-book-summary-k">{{ isFa ? 'وضعیت رزرو' : 'Booking status' }}</span><span class="rb-book-summary-v">{{ bookingStatusLabel }}</span></li>
      <li>
        <span class="rb-book-summary-k">{{ isFa ? 'امکان لغو' : 'Can cancel' }}</span>
        <span class="rb-book-summary-v">{{ canCancelLabel }}</span>
      </li>
      <li v-if="deadlineLabel">
        <span class="rb-book-summary-k">{{ isFa ? 'مهلت لغو' : 'Cancel deadline' }}</span>
        <span class="rb-book-summary-v"><bdi>{{ deadlineLabel }}</bdi></span>
      </li>
      <li v-if="outcomeLabel">
        <span class="rb-book-summary-k">{{ isFa ? 'نتیجه لغو' : 'Cancel outcome' }}</span>
        <span class="rb-book-summary-v">{{ outcomeLabel }}</span>
      </li>
    </ul>
    <ul v-if="isCareSession && carePreview" class="rb-book-summary">
      <li><span class="rb-book-summary-k">{{ isFa ? 'شماره جلسه' : 'Session' }}</span><span class="rb-book-summary-v">{{ carePreview.session_number || '—' }}</span></li>
      <li><span class="rb-book-summary-k">{{ isFa ? 'وضعیت جلسه' : 'Session status' }}</span><span class="rb-book-summary-v">{{ carePreview.state === 'date_fixed' ? (isFa ? 'تأییدشده' : 'Confirmed') : carePreview.state }}</span></li>
      <li><span class="rb-book-summary-k">{{ isFa ? 'امکان لغو' : 'Can cancel' }}</span><span class="rb-book-summary-v">{{ canCancelCareSession ? (isFa ? 'بله' : 'Yes') : (isFa ? 'خیر' : 'No') }}</span></li>
      <li v-if="deadlineLabel"><span class="rb-book-summary-k">{{ isFa ? 'مهلت لغو' : 'Cancel deadline' }}</span><span class="rb-book-summary-v"><bdi>{{ deadlineLabel }}</bdi></span></li>
    </ul>

    <div v-if="!loading && !needsLogin" class="rb-ob-status-panel">
      <p class="rb-match-outcome-body">{{ policyMessage }}</p>
    </div>

    <div class="rb-ob-actions">
      <button
        v-if="canCancelCareSession"
        class="rb-ob-submit"
        type="button"
        :disabled="saving"
        @click="confirmCareSession"
      >
        {{ saving ? $t('intake.portal.loading') : (isFa ? 'لغو جلسه و بازگرداندن اعتبار' : 'Cancel session and restore credit') }}
      </button>
      <button
        v-if="canCancelUnpaid"
        class="rb-ob-submit"
        type="button"
        :disabled="saving"
        @click="confirmUnpaid"
      >
        {{ saving ? $t('intake.portal.loading') : (isFa ? 'لغو رزرو پرداخت‌نشده' : 'Cancel unpaid booking') }}
      </button>
      <button
        v-if="canCancelConfirmed"
        class="rb-ob-submit"
        type="button"
        :disabled="saving"
        @click="confirmConfirmed"
      >
        {{ saving ? $t('intake.portal.loading') : (isFa ? 'لغو جلسه و بازگرداندن اعتبار' : 'Cancel session and restore credit') }}
      </button>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" :disabled="loading" @click="load">
        {{ isFa ? 'به‌روزرسانی وضعیت' : 'Refresh status' }}
      </button>
      <router-link
        v-if="creditRestored"
        class="rb-ob-submit"
        :to="reserveLink"
      >
        {{ isFa ? 'رزرو دوباره با اعتبار' : 'Book again with credit' }}
      </router-link>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="keep">
        {{ isFa ? 'بازگشت' : 'Back' }}
      </button>
      <p class="rb-ob-status" role="status" aria-live="polite">{{ formStatus }}</p>
    </div>
  </div>
</template>

<script>
import {
  cancelConfirmedBookingOperation,
  cancelUnpaidBookingOperation,
  cancelCareSession,
  fetchBookingCancellationPreview,
  fetchBookingOperations,
  fetchCareSessionCancellation,
  fetchProfile,
  formatRescheduleDeadline,
  hasAuthToken,
  reserveSessionRoute,
} from '@/intake/portalApi'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export default {
  name: 'SessionCancelStep',
  data() {
    return {
      loading: false,
      saving: false,
      needsLogin: false,
      snapshot: null,
      preview: null,
      carePreview: null,
      careErrorCode: '',
      formStatus: '',
      creditRestored: false,
    }
  },
  computed: {
    lang() { return this.$route.params.lang || 'fa' },
    isFa() { return this.lang === 'fa' },
    reserveLink() { return reserveSessionRoute(this.lang) },
    sessionId() {
      const id = this.routeBookingHint()
      return id && /^\d+$/.test(String(id)) ? String(id) : null
    },
    isCareSession() { return !!this.sessionId },
    booking() {
      return (this.preview && this.preview.booking)
        || (this.snapshot && this.snapshot.booking)
        || null
    },
    canCancelUnpaid() {
      return !!(this.preview && this.preview.action_allowed === true && this.preview.action === 'CANCEL_UNPAID_BOOKING')
    },
    canCancelConfirmed() {
      return !!(this.preview && this.preview.action_allowed === true && this.preview.action === 'CANCEL_CONFIRMED_RESTORE_PACKAGE')
    },
    canCancelCareSession() {
      return !!(this.carePreview && this.carePreview.cancel_allowed === true)
    },
    isCancelledBooking() {
      const state = String((this.booking && (this.booking.state || this.booking.client_status)) || '').toUpperCase()
      return state === 'CANCELLED' || state === 'CANCELED'
    },
    canCancelLabel() {
      if (this.canCancelUnpaid || this.canCancelConfirmed) return this.isFa ? 'بله' : 'Yes'
      if (!this.preview && this.loading) return '—'
      return this.isFa ? 'خیر' : 'No'
    },
    bookingStatusLabel() {
      const raw = (this.booking && (this.booking.client_status || this.booking.state)) || ''
      const mapFa = {
        CONFIRMED: 'تأییدشده',
        AWAITING_PAYMENT: 'در انتظار پرداخت',
        PROCESSING: 'در حال پردازش',
        PAYMENT_LATE: 'نیاز به پیگیری مالی',
        PAYMENT_FAILED: 'پرداخت ناموفق',
        CANCELLED: 'لغوشده',
      }
      const mapEn = {
        CONFIRMED: 'Confirmed',
        AWAITING_PAYMENT: 'Awaiting payment',
        PROCESSING: 'Processing',
        PAYMENT_LATE: 'Needs finance follow-up',
        PAYMENT_FAILED: 'Payment failed',
        CANCELLED: 'Cancelled',
      }
      return (this.isFa ? mapFa : mapEn)[raw] || raw || '—'
    },
    deadlineLabel() {
      const preview = this.isCareSession ? this.carePreview : this.preview
      if (!(preview && preview.deadline)) return ''
      return formatRescheduleDeadline(preview.deadline, this.lang)
    },
    outcomeLabel() {
      if (this.canCancelConfirmed) {
        return this.isFa
          ? 'بدون بازگشت پول؛ اعتبار جلسه برمی‌گردد'
          : 'No cash refund; session credit restored'
      }
      if (this.canCancelUnpaid) {
        return this.isFa ? 'بدون اثر مالی (پرداخت‌نشده)' : 'No financial effect (unpaid)'
      }
      return ''
    },
    reasonCode() {
      return (this.preview && this.preview.reason_code)
        || (!this.booking && !this.loading ? 'NO_SERVER_BOOKING' : '')
    },
    policyMessage() {
      if (this.isCareSession) {
        if (!this.carePreview) return !this.loading ? this.humanReason(this.careErrorCode || 'CANCELLATION_STATUS_FAILED') : ''
        if (this.canCancelCareSession) return this.isFa
          ? 'با لغو این جلسه، زمان آزاد و اعتبار بسته برای رزرو دوباره در دسترس می‌شود. بازپرداخت نقدی انجام نمی‌شود.'
          : 'Cancelling releases the time and restores package credit. No cash refund is made.'
        return this.humanReason(this.carePreview.reason_code || 'SESSION_STATE_NOT_CANCELLABLE')
      }
      if (!this.booking && !this.loading) {
        return this.isFa ? 'رزرو تأییدشده‌ای برای لغو پیدا نشد.' : 'No booking was found to cancel.'
      }
      if (this.canCancelUnpaid) {
        return this.isFa
          ? 'این رزرو هنوز پرداخت تأییدشده ندارد. لغو فقط رزرو را آزاد می‌کند و پول یا اعتبار جدیدی برنمی‌گرداند.'
          : 'No payment is verified yet. Cancellation only releases the unpaid booking and creates no refund or credit.'
      }
      if (this.canCancelConfirmed) {
        return this.isFa
          ? 'می‌توانی این جلسهٔ تأییدشده را تا مهلت اعلام‌شده لغو کنی. پول نقد برنمی‌گردد، ولی اعتبار جلسه (بسته) به حسابت برمی‌گردد تا بتوانی دوباره رزرو کنی.'
          : 'You can cancel this confirmed session before the deadline. There is no cash refund, but your session package credit is restored so you can book again.'
      }
      return this.humanReason(this.reasonCode)
    },
  },
  mounted() { this.load() },
  methods: {
    humanReason(code) {
      const mapFa = {
        CANCEL_TOO_LATE: 'مهلت لغو این جلسه گذشته است. برای کمک با پشتیبانی تماس بگیر.',
        BOOKING_STATE_NOT_CANCELLABLE: 'وضعیت فعلی این رزرو اجازهٔ لغو از این صفحه را نمی‌دهد.',
        CANCEL_SESSION_TIME_UNKNOWN: 'زمان جلسه از سرور قابل خواندن نیست. لطفاً بعداً دوباره امتحان کن.',
        CANCEL_TEMPLATE_MISSING: 'الان امکان لغو این جلسه وجود ندارد. لطفاً بعداً دوباره امتحان کن.',
        CANCEL_CASH_REFUND_UNSUPPORTED: 'لغو با بازپرداخت نقدی هنوز در دسترس نیست.',
        CONFIRMED_CANCEL_COMMIT_DISABLED: 'الان امکان لغو این جلسه وجود ندارد. لطفاً بعداً دوباره امتحان کن.',
        ALREADY_CANCELLED: 'این رزرو قبلاً لغو شده است.',
        NO_CANCELLATION_ACTION: 'برای وضعیت فعلی، عملیات لغو ارائه نمی‌شود.',
        NO_SERVER_BOOKING: 'رزرو تأییدشده‌ای برای لغو پیدا نشد.',
        CANCELLATION_STATUS_FAILED: 'خواندن وضعیت لغو ممکن نشد.',
        CANCELLATION_FAILED: 'لغو انجام نشد. لطفاً دوباره امتحان کن.',
        CARE_SESSION_NOT_FOUND: 'این جلسه برای لغو پیدا نشد.',
        SESSION_NOT_OWNED: 'شما دسترسی به لغو این جلسه را ندارید.',
        SESSION_STATE_NOT_CANCELLABLE: 'وضعیت فعلی جلسه اجازهٔ لغو را نمی‌دهد.',
      }
      const mapEn = {
        CANCEL_TOO_LATE: 'The cancellation deadline for this session has passed. Contact support if you need help.',
        BOOKING_STATE_NOT_CANCELLABLE: 'The current booking state cannot be cancelled here.',
        CANCEL_SESSION_TIME_UNKNOWN: 'The session time could not be read from the server. Try again later.',
        CANCEL_TEMPLATE_MISSING: 'Cancellation is unavailable right now. Please try again later.',
        CANCEL_CASH_REFUND_UNSUPPORTED: 'Cash-refund cancellation is not available.',
        CONFIRMED_CANCEL_COMMIT_DISABLED: 'Cancellation is unavailable right now. Please try again later.',
        ALREADY_CANCELLED: 'This booking is already cancelled.',
        NO_CANCELLATION_ACTION: 'No cancellation action is available for the current state.',
        NO_SERVER_BOOKING: 'No booking was found to cancel.',
        CANCELLATION_STATUS_FAILED: 'Could not load cancellation status.',
        CANCELLATION_FAILED: 'Cancellation failed. Please try again.',
        CARE_SESSION_NOT_FOUND: 'This session was not found for cancellation.',
        SESSION_NOT_OWNED: 'You do not have permission to cancel this session.',
        SESSION_STATE_NOT_CANCELLABLE: 'The current session state cannot be cancelled.',
      }
      const map = this.isFa ? mapFa : mapEn
      if (code && map[code]) return map[code]
      return this.isFa
        ? 'الان امکان لغو این جلسه وجود ندارد. در صورت نیاز با پشتیبانی تماس بگیر.'
        : 'This session cannot be cancelled right now. Contact support if you need help.'
    },
    routeBookingHint() {
      const q = this.$route.query || {}
      return q.booking_id || q.bookingId || q.id || null
    },
    cancellationFailureCode(error) {
      const response = error && error.response
      if (response && response.status === 404) return 'CARE_SESSION_NOT_FOUND'
      if (!response || response.status >= 500) return 'CANCELLATION_STATUS_FAILED'
      return (response.data && response.data.data && response.data.data.reason_code) || 'CANCELLATION_STATUS_FAILED'
    },
    resolveBookingId(snapshot) {
      const hint = this.routeBookingHint()
      if (hint && UUID_RE.test(String(hint))) return String(hint)
      if (snapshot && snapshot.booking && snapshot.booking.booking_id) return snapshot.booking.booking_id
      return null
    },
    load() {
      this.needsLogin = !hasAuthToken()
      if (this.needsLogin) return Promise.resolve()
      this.loading = true
      this.formStatus = ''
      this.snapshot = null
      this.preview = null
      this.carePreview = null
      this.careErrorCode = ''
      const hint = this.routeBookingHint()
      if (this.sessionId) {
        return fetchCareSessionCancellation(this.sessionId)
          .then((preview) => { this.carePreview = preview || null })
          .catch((e) => {
            if (e && e.response && e.response.status === 401) this.needsLogin = true
            this.careErrorCode = this.cancellationFailureCode(e)
            this.formStatus = this.humanReason(this.careErrorCode)
          })
          .finally(() => { this.loading = false })
      }
      const sessionOpts = (hint && /^\d+$/.test(String(hint))) ? {userSessionId: hint} : {}
      return fetchBookingOperations(sessionOpts).then((snapshot) => {
        this.snapshot = snapshot || null
        const id = this.resolveBookingId(snapshot)
        if (!id) return null
        return fetchBookingCancellationPreview(id).then((preview) => { this.preview = preview })
      }).catch((e) => {
        this.formStatus = this.humanReason((e && e.message) || 'CANCELLATION_STATUS_FAILED')
      }).finally(() => { this.loading = false })
    },
    confirmUnpaid() {
      if (!this.canCancelUnpaid || this.saving || !this.booking) return
      this.saving = true
      this.formStatus = ''
      return cancelUnpaidBookingOperation(this.booking.booking_id)
        .then(() => {
          this.formStatus = this.isFa ? 'رزرو پرداخت‌نشده لغو شد.' : 'Unpaid booking cancelled.'
          this.goSessionsList()
        })
        .catch((e) => {
          const code = (e && e.response && e.response.data && e.response.data.data && e.response.data.data.reason_code)
            || (e && e.message)
            || 'CANCELLATION_FAILED'
          this.formStatus = this.humanReason(String(code))
        })
        .finally(() => { this.saving = false })
    },
    confirmConfirmed() {
      if (!this.canCancelConfirmed || this.saving || !this.booking) return
      this.saving = true
      this.formStatus = ''
      return cancelConfirmedBookingOperation(this.booking.booking_id)
        .then((result) => {
          this.preview = result
          this.creditRestored = true
          this.formStatus = this.isFa
            ? 'جلسه لغو شد. اعتبار بسته برای رزرو دوباره در دسترس است. پول نقد برنگشته است.'
            : 'Session cancelled. Package credit is available to book again. No cash was refunded.'
          return fetchProfile().catch(() => null).then(() => {
            this.goSessionsList()
          })
        })
        .catch((e) => {
          const code = (e && e.response && e.response.data && e.response.data.data && e.response.data.data.reason_code)
            || (e && e.message)
            || 'CANCELLATION_FAILED'
          this.formStatus = this.humanReason(String(code))
        })
        .finally(() => { this.saving = false })
    },
    confirmCareSession() {
      if (!this.canCancelCareSession || this.saving) return
      this.saving = true
      this.formStatus = ''
      return cancelCareSession(this.sessionId)
        .then(() => {
          this.creditRestored = true
          this.formStatus = this.isFa
            ? 'جلسه لغو شد. اعتبار بسته برای رزرو دوباره در دسترس است.'
            : 'Session cancelled. Package credit is available to book again.'
          return fetchProfile().catch(() => null).then(() => this.goSessionsList())
        })
        .catch((e) => {
          const code = (e && e.response && e.response.data && e.response.data.data && e.response.data.data.reason_code) || (e && e.message) || 'CANCELLATION_FAILED'
          this.formStatus = this.humanReason(String(code))
        })
        .finally(() => { this.saving = false })
    },
    goSessionsList() {
      this.$router.push({name: 'portalSessions', params: {lang: this.lang}})
    },
    keep() {
      // After cancel the session is gone from the live list — never bounce back to its detail page.
      if (this.creditRestored || this.isCancelledBooking) {
        this.goSessionsList()
        return
      }
      const q = this.$route.query || {}
      if (q.id) {
        this.$router.push({name: 'portalSessionDetail', params: {lang: this.lang}, query: {id: String(q.id)}})
        return
      }
      this.goSessionsList()
    },
  },
}
</script>
